import { action } from '@ember/object';
import type Owner from '@ember/owner';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';

import $ from 'jquery';
import moment from 'moment';

import { uniqueId } from 'webapp/utils/common';

import themed from 'webapp/decorators/themed';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import willDestroy from 'webapp/modifiers/will-destroy';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import FormInputComponent from 'webapp/components/form/input';

import type Account from 'webapp/services/account';

interface Args {
	value?: string | null;
	dateFormat?: string;
	timeFormat?: string;
	disableDatePicker?: boolean;
	disableTimePicker?: boolean;
	disablePast?: boolean; // changing this during components lifecycle does not work bcos of library's setOption minDate issue
	disableFuture?: boolean; // changing this during components lifecycle does not work bcos of library's setOption maxDate issue
	disableToday?: boolean; // changing this during components lifecycle does not work bcos of library's setOption maxDate issue
	isDisabled?: boolean;
	extraClass?: string;
	theme?: string;
	size?: string;
	placeholder?: string;
	hideFloatLabel?: boolean;
	showClearButton?: boolean;
	onChange: (value: string) => void;
}

@themed()
export default class DatetimePickerComponent extends Component<Args> {
	@service declare account: Account;

	@tracked value: string;

	dateEl: JQuery<HTMLInputElement> | null = null;
	inputName: string;
	dateFormat: string;
	timeFormat: string;
	datetimeFormat: string;
	showClearButton: boolean;

	declare themeClass: string; // from @themed
	declare sizeClass: string; // from @themed

	private previousValue: string;

	constructor(owner: Owner, args: Args) {
		super(owner, args);

		this.value = this.previousValue = this.args.value || '';

		this.dateFormat = this.args.dateFormat || this.account.accountDateFormat;

		this.timeFormat = this.args.timeFormat || this.account.accountTimeFormat;

		this.datetimeFormat = this.dateFormat + ' ' + this.timeFormat;

		this.showClearButton = this.args.showClearButton || false;

		this.inputName = `dtp-${uniqueId()}`;
	}

	get hasDatePicker(): boolean {
		return !this.args.disableDatePicker;
	}

	get hasTimePicker(): boolean {
		return !this.args.disableTimePicker;
	}

	get isTimePicker(): boolean {
		return this.hasTimePicker && !this.hasDatePicker;
	}

	get isDatePicker(): boolean {
		return this.hasDatePicker && !this.hasTimePicker;
	}

	get isDateTimePicker(): boolean {
		return this.hasDatePicker && this.hasTimePicker;
	}

	@action
	setupPicker(node: HTMLElement): void {
		const el = $(node),
			dateEl: JQuery<HTMLInputElement> = el.find('.datetime-picker-input');

		let options: Record<string, unknown> = {},
			timeOptions: Record<string, unknown> = {
				hours: true,
				minutes: true,
				seconds: false,
				ampm: true,
				inputFormat: this.timeFormat,
			};

		if (!dateEl.length) {
			return;
		}

		this.dateEl = dateEl;

		if (this.hasTimePicker) {
			timeOptions = {
				hours: true,
				minutes: true,
				seconds: false,
				ampm: true,
				inputFormat: this.timeFormat,
			};

			// We add this onChange fn to handle changes when we initialize timepickeralone,
			// otherwise onChange function from periodpicker is used when we have datetimepicker
			if (!this.hasDatePicker) {
				timeOptions.onChange = (value: string /* datetime: Date */) => {
					this.previousValue = value;
					this.args.onChange(value);
				};
			}
		}

		if (this.hasDatePicker) {
			const momentBase = moment();

			options = {
				placement: 'auto auto',
				animation: false, // when true and user clicks on input several times, popup doesnt show up
				resizeButton: false,
				formatDate: this.dateFormat,
				formatDecoreDateWithYear: this.account.accountDateFormatHumanReadable, // in the button
				fullsize: false,
				fullsizeOnDblClick: false,
				draggable: false,
				yearsLine: false,
				title: false,
				mousewheel: false,
				dayOfWeekStart: this.account.firstDayOfWeek || 7, // we have Mon - 1, Sun - 0, picker accepts Mon - 1, Sun - 7
				minDate: this.getMinDate(),
				maxDate: this.getMaxDate(),
				cells: [1, 1],
				monthWidthInPixels: 250,
				monthHeightInPixels: 190, // for 5 week lines
				someYOffset: -50,
				fullYearsInFullscreen: true,
				norange: true,
				hideAfterSelect: true,
				clearButton: this.showClearButton,
				withoutBottomPanel: !this.showClearButton,
				showDatepickerInputs: !this.showClearButton,
				timepicker: this.hasTimePicker, // use timepicker within periodpicker
				startMonth: momentBase.month() + 1, // january is 0
				startYear: momentBase.year(),
				onClearButtonClick: () => {
					this.onChange(el);

					this.updatePeriodPickerPlaceholder(el);
				},
				onAfterShow: () => {
					const periodPickerBox = $('.period_picker_box');

					periodPickerBox.addClass(this.themeClass);
					// minimize picker when we close it, otherwise it stays fullscreen after reopening
					if (periodPickerBox.hasClass('period_picker_maximize')) {
						periodPickerBox.removeClass('period_picker_maximize');

						this.dateEl?.periodpicker('regenerate');
					}

					// inject ui-icon for maximize button if not already present
					const maxMinBtn = periodPickerBox.find('.period_picker_max_min');
					if (maxMinBtn.length && !maxMinBtn.find('ui-icon').length) {
						maxMinBtn.prepend('<ui-icon name="SheetMaximize" size="sm"></ui-icon>');
					}

					// replace font icons with UiIcon for xdsoft calendar navigation arrows
					const xdsoftPrev = periodPickerBox.find('.xdsoft_navigate_prev');
					if (xdsoftPrev.length && !xdsoftPrev.find('ui-icon').length) {
						xdsoftPrev.append('<ui-icon name="ChevronLeft" size="xs"></ui-icon>');
					}
					const xdsoftNext = periodPickerBox.find('.xdsoft_navigate_next');
					if (xdsoftNext.length && !xdsoftNext.find('ui-icon').length) {
						xdsoftNext.append('<ui-icon name="ChevronRight" size="xs"></ui-icon>');
					}

					// replace font icon with UiIcon for period date separator (arrow between dates in popup)
					const periodDateSeparator = periodPickerBox.find(
						'.period_picker_date_separator',
					);
					if (periodDateSeparator.length && !periodDateSeparator.find('ui-icon').length) {
						periodDateSeparator.append(
							'<ui-icon name="ArrowRight" size="xs"></ui-icon>',
						);
					}

					this.updatePeriodPickerPlaceholder(el);
				},
				onChange: () => {
					this.onChange(el);
				},
			};

			if (this.hasTimePicker) {
				options.timepickerOptions = timeOptions;
				options.formatDateTime = this.datetimeFormat;
				options.formatDecoreDateTimeWithYear = this.account.accountDatetimeFormat;

				// Those props are set so that we can select time and date.
				// Picker hides after clicking on OK button - also we show date input.
				options.hideAfterSelect = false;
				options.withoutBottomPanel = false;
			}
		}

		let inputSelector;
		if (this.isDateTimePicker || this.isDatePicker) {
			inputSelector = '.period_picker_input';

			dateEl.periodpicker(options);

			this.updatePeriodPickerPlaceholder(el);
			dateEl.periodpicker('picker').options = options;

			// Replace font icon with UiIcon for period button dash (arrow between dates in input)
			const periodButtonDash = el.find('.period_button_dash');
			if (periodButtonDash.length && !periodButtonDash.find('ui-icon').length) {
				periodButtonDash.append('<ui-icon name="ArrowRight" size="xs"></ui-icon>');
			}
		} else {
			inputSelector = '.datetime-picker-input';
			const val = this.value || ''; // for showing placeholder in input if no time is set

			dateEl.timepickeralone(timeOptions);
			el.find(inputSelector).val(val).prop({ readonly: true });
		}

		el.find(inputSelector).addClass(this.themeClass);
		el.find(inputSelector).addClass(this.sizeClass);

		this.togglePickerAvailability();

		// preparing methods for teardown
		let teardownFn: () => void;
		if (this.isDateTimePicker || this.isDatePicker) {
			teardownFn = () => {
				const picker = dateEl.periodpicker('picker');
				if (picker) {
					picker.destroy();
				}
			};
		} else {
			teardownFn = () => {
				dateEl.timepickeralone('destroy');
			};
		}
		this.teardownFn = teardownFn;
	}

	updatePeriodPickerPlaceholder(el: JQuery): void {
		if (!this.value && this.args.placeholder) {
			el.find('.period_button_content_body')
				.text(this.args.placeholder)
				.attr('style', 'color: var(--databox-neutral-400)');
		}
	}

	@action
	togglePickerAvailability(): void {
		const dateEl = this.dateEl;
		if (!dateEl) {
			return;
		}
		if (this.isDateTimePicker || this.isDatePicker) {
			const availability = this.args.isDisabled ? 'disable' : 'enable';

			dateEl.periodpicker(availability);
		} else {
			dateEl.prop({ disabled: this.args.isDisabled });
		}
	}

	@action
	updateValue() {
		const dateEl = this.dateEl;
		if (!dateEl) {
			return;
		}
		const newValue = this.args.value || '';
		if (this.previousValue !== newValue) {
			this.previousValue = newValue;
			// dateEl.periodpicker('change');
			dateEl.periodpicker('value', newValue);
		}
	}

	private getMinDate() {
		let momentBase = moment();
		if (!this.args.disablePast) {
			momentBase = moment().subtract(10, 'year');
		}
		return momentBase.format(this.dateFormat);
	}

	private getMaxDate() {
		return this.args.disableFuture
			? moment()
					.add(this.args.disableToday ? 0 : 1, 'days')
					.format(this.dateFormat)
			: moment().add(10, 'year').format(this.dateFormat);
	}

	private onChange(el: JQuery) {
		const dateEl = this.dateEl;
		if (dateEl) {
			this.value = dateEl.get(0)?.value || '';
			el.find('.period_button_content_body').removeAttr('style');
		}
		const value = this.value;
		this.previousValue = value;
		this.args.onChange(value);
	}

	private teardownFn: (() => void) | null = null;

	@action
	teardownPicker(): void {
		if (this.teardownFn) {
			this.teardownFn();
		}
	}
	<template>
		<div
			class="datetime-picker {{@extraClass}}"
			{{didInsert this.setupPicker}}
			{{didUpdate this.togglePickerAvailability @isDisabled}}
			{{didUpdate this.updateValue @value}}
			{{willDestroy this.teardownPicker}}
		>
			<CommonUiIconComponent @name="CalendarExclamation" @size="lg" @class="datetime-icon" />
			<CommonUiIconComponent @name="ChevronDown" @size="xs" @class="datetime-arrow" />
			<FormInputComponent
				name={{this.inputName}}
				class="datetime-picker-input {{this.themeClass}} {{this.sizeClass}}"
				placeholder={{@placeholder}}
				@value={{this.value}}
				@hideFloatLabel={{@hideFloatLabel}}
			/>

		</div>
	</template>
}
